import { useState } from 'react';
import { X, Copy, CheckCircle2, Share2, MessageCircle, Mail, Send, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

// Social media icons as SVG components
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const ReferralShareDialog = ({ isOpen, onClose, referralCode, shareMessage, shareUrl }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const fullShareMessage = shareMessage || `Join Mint a Milli and start your gold savings journey! Use my referral code ${referralCode} to get rewards on your first booking. ${shareUrl || 'https://mintamilli.mothersgold.com'}`;
  const encodedMessage = encodeURIComponent(fullShareMessage);
  const encodedUrl = encodeURIComponent(shareUrl || 'https://mintamilli.mothersgold.com');

  const handleCopyCode = () => {
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    toast.success('Referral code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyMessage = () => {
    navigator.clipboard.writeText(fullShareMessage);
    toast.success('Share message copied!');
  };

  const shareOptions = [
    {
      name: 'WhatsApp',
      icon: WhatsAppIcon,
      color: 'bg-[#25D366] hover:bg-[#20BA5A]',
      url: `https://wa.me/?text=${encodedMessage}`,
    },
    {
      name: 'Twitter',
      icon: TwitterIcon,
      color: 'bg-black hover:bg-gray-800',
      url: `https://twitter.com/intent/tweet?text=${encodedMessage}`,
    },
    {
      name: 'Telegram',
      icon: TelegramIcon,
      color: 'bg-[#0088cc] hover:bg-[#0077b5]',
      url: `https://t.me/share/url?url=${encodedUrl}&text=${encodedMessage}`,
    },
    {
      name: 'Facebook',
      icon: FacebookIcon,
      color: 'bg-[#1877F2] hover:bg-[#166FE5]',
      url: `https://www.facebook.com/sharer/sharer.php?quote=${encodedMessage}`,
    },
    {
      name: 'Email',
      icon: Mail,
      color: 'bg-gray-600 hover:bg-gray-700',
      url: `mailto:?subject=Join%20Mint%20a%20Milli%20-%20Start%20Your%20Gold%20Journey&body=${encodedMessage}`,
    },
  ];

  const handleShare = (option) => {
    window.open(option.url, '_blank', 'width=600,height=400');
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Mint a Milli',
          text: fullShareMessage,
          url: shareUrl || 'https://mintamilli.mothersgold.com',
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header with gold gradient */}
        <div className="bg-gradient-to-r from-amber-500 to-yellow-500 px-6 py-5 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Share2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Share & Earn</h2>
              <p className="text-white/80 text-sm">Invite friends, get rewards!</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Referral Code Display */}
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-4 border-2 border-amber-200 mb-5">
            <p className="text-xs text-amber-700 font-medium mb-2 uppercase tracking-wide">Your Referral Code</p>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-white rounded-lg px-4 py-3 border border-amber-300">
                <p className="text-2xl font-bold tracking-[0.2em] text-amber-800 text-center">{referralCode}</p>
              </div>
              <button
                onClick={handleCopyCode}
                className={`p-3 rounded-xl transition-all ${
                  copied 
                    ? 'bg-green-500 hover:bg-green-600' 
                    : 'bg-amber-500 hover:bg-amber-600'
                }`}
                title="Copy code"
              >
                {copied ? (
                  <CheckCircle2 className="w-6 h-6 text-white" />
                ) : (
                  <Copy className="w-6 h-6 text-white" />
                )}
              </button>
            </div>
          </div>

          {/* Share Message Preview */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Share Message</p>
              <button 
                onClick={handleCopyMessage}
                className="text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1"
              >
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 max-h-24 overflow-y-auto">
              <p className="text-sm text-gray-600 leading-relaxed">{fullShareMessage}</p>
            </div>
          </div>

          {/* Share Options Grid */}
          <div className="mb-4">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">Share Via</p>
            <div className="grid grid-cols-5 gap-2">
              {shareOptions.map((option) => (
                <button
                  key={option.name}
                  onClick={() => handleShare(option)}
                  className={`${option.color} p-3 rounded-xl transition-all flex flex-col items-center justify-center gap-1.5 text-white`}
                  title={option.name}
                >
                  <option.icon />
                  <span className="text-[10px] font-medium">{option.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Native Share Button (Mobile) */}
          {navigator.share && (
            <button
              onClick={handleNativeShare}
              className="w-full py-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-semibold rounded-xl flex items-center justify-center gap-2 hover:from-amber-600 hover:to-yellow-600 transition-all shadow-lg shadow-amber-200"
            >
              <Send className="w-5 h-5" />
              More Sharing Options
            </button>
          )}

          {/* Reward Info */}
          <div className="mt-5 p-3 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm text-green-700 text-center">
              <span className="font-semibold">Both you and your friend get rewards</span> when they make their first booking!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralShareDialog;
